import { Bundle, CollectionProduct } from '@spiffcommerce/core'
import { SpiffCommerce3DPreviewService } from '@spiffcommerce/preview'
import { RefObject, useEffect, useState } from 'react'
import { useShopifyMetadata } from './useShopifyMetadata'
import { useAppContext } from '../context/AppContext'
import { createWorkflowExperiencesForPendants, updateWorkflowExperienceTransformsForPendants } from '../helpers/spiffTransforms'
import { client } from '../../helpers/spiff'

export const preLoadedNecklacePendantProducts = ['letter', 'birthstone', 'charm']
export const preLoadedBraceletPendantProducts = ['letter', 'birthstone', 'charm']

const basePreviewService = new SpiffCommerce3DPreviewService()

export const useSpiffClient = (canvasRef: RefObject<HTMLCanvasElement>) => {
  const { setAvailablePreLoadedPendants, setIsOnBodyPreview } = useAppContext()
  const shopifyMetadata = useShopifyMetadata()

  const [bundle, setBundle] = useState<Bundle | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const initializeBundle = async (collectionId = shopifyMetadata?.spiffCollectionId) => {
    setIsLoading(true)

    const newBundle = await client.getNewBundle(collectionId, undefined, {
      graphql: {
        productCollection: {
          eagerFetchProducts: true
        }
      }
    })

    const collection = newBundle.getProductCollection() // Contains all the products in the bundle
    await collection?.fetchProducts()

    if (!collection) return setIsLoading(false)
    const baseProduct = collection.getProducts().find((product) => !product.getResource().productTags?.find((tag) => tag.name === 'pendant')) // Update to find product by tag
    const baseProductType = baseProduct?.getResource().name?.toLowerCase().includes('bracelet') ? 'bracelet' : 'necklace'
    const pendantProducts = collection.getProducts().filter((product) => product.getResource().productTags?.find((tag) => tag.name === 'pendant')) // Product will be tagged with 'pendant' in Spiff

    setAvailablePreLoadedPendants(baseProductType === 'bracelet' ? preLoadedBraceletPendantProducts : preLoadedNecklacePendantProducts)
    if (!baseProduct) return setIsLoading(false)
    const baseWorkflowExperience = await getWorkflowExperience(baseProduct)

    if (!baseWorkflowExperience) return setIsLoading(false)

    if (bundle) {
      bundle.getWorkflowExperiences().forEach((workflow) => {
        try {
          workflow.getWorkflowManager().ejectFromPreviewService()
        } catch (error) {
          console.error('error', error)
        }
      })
    }

    basePreviewService.updatePreviewOptions({
      ...baseWorkflowExperience.getWorkflowManager().getWorkflow().globalPreviewConfig,
      transparentBackground: false,
      noPan: true
    })

    if (!canvasRef.current) return setIsLoading(false)

    basePreviewService.registerView(canvasRef.current)

    // Look for pendant steps in the current workflow and then set a piece of state based on default number of pendants
    setIsOnBodyPreview(false)

    try {
      await baseWorkflowExperience.getWorkflowManager().injectIntoPreviewService(basePreviewService)
      await Promise.all([
        basePreviewService.getInitializationPromise(),
        baseWorkflowExperience.getWorkflowManager().getModelContainer()?.getInitializationPromise()
      ])
      await basePreviewService.animateToLastCameraFocus()
    } catch (error) {
      console.error('error', error)
      return setIsLoading(false)
    }

    const pendantQuantity = Number(
      baseWorkflowExperience
        .getSteps()
        .filter((step) => step.getTags().includes('pendants'))[0]
        ?.getCurrentVariant()
        ?.getName()
    )
    const pendantWorkflowExperienceAndTransforms = await createWorkflowExperiencesForPendants({
      baseWorkflowExperience,
      bundle: newBundle,
      getProduct: (index) => {
        const arrayToUse = baseProductType === 'bracelet' ? preLoadedBraceletPendantProducts : preLoadedNecklacePendantProducts
        const product = pendantProducts.find((product) =>
          product
            .getName()
            .toLowerCase()
            .includes(arrayToUse[index % arrayToUse.length])
        )
        return product
      },
      quantity: pendantQuantity
    })

    if (!pendantWorkflowExperienceAndTransforms) {
      console.error('no workflow experience and transforms')
      return
    }

    const pendantWorkflowExperiences = pendantWorkflowExperienceAndTransforms.map((item) => item.workflowExperience)

    await newBundle.addWorkflowExperiences([baseWorkflowExperience, ...pendantWorkflowExperiences])
    await updateWorkflowExperienceTransformsForPendants({
      workflowExperienceAndTransforms: pendantWorkflowExperienceAndTransforms,
      isOnBodyPreview: false,
      bundle: newBundle,
      previewService: basePreviewService
    })

    setBundle(newBundle)
    setIsLoading(false)
  }

  const getWorkflowExperience = async (product: CollectionProduct) => {
    if (!product) return
    const integrationProductId = product.getIntegrationByType('Shopify' as any, shopifyMetadata?.shopifyStoreUrl).id
    const workflowId = product.getDefaultWorkflow().getId()

    const workflowExperience = await client.getWorkflowExperiences(
      [
        {
          integrationProductId: integrationProductId,
          workflowId: workflowId,
          type: 'integration',
          workflowConfiguration: {
            singleVariantsRenderable: true
          }
        }
      ],
      {
        assets: {
          metadata: true
        }
      }
    )

    return workflowExperience[0]
  }

  const clearSelections = async () => {
    await initializeBundle(shopifyMetadata!.spiffCollectionId)
  }

  useEffect(() => {
    if (!shopifyMetadata) return

    initializeBundle(shopifyMetadata.spiffCollectionId)
  }, [])

  return {
    bundle,
    previewService: basePreviewService,
    isLoading,
    setIsLoading,
    initializeBundle,
    getWorkflowExperience,
    clearSelections
  }
}
